import { get, onValue } from 'firebase/database';

const STREAM_RETENTION_MS = 60 * 1000;
const valueCache = new Map();
const streams = new Map();

function cachedSnapshot(value) {
  return {
    exists: () => value !== null && value !== undefined,
    val: () => value,
  };
}

function stopStream(key, stream) {
  if (typeof stream.unsubscribe === 'function') stream.unsubscribe();
  stream.unsubscribe = null;
  stream.active = false;
  stream.detachTimer = null;
  if (stream.listeners.size === 0) streams.delete(key);
}

export function subscribeCachedValue(reference, cacheKey, onNext, onError) {
  const key = String(cacheKey || '');
  if (!key) throw new Error('A cache key is required for Firebase subscriptions.');

  let stream = streams.get(key);
  if (!stream) {
    stream = {
      active: false,
      detachTimer: null,
      listeners: new Set(),
      unsubscribe: null,
    };
    streams.set(key, stream);
  }

  if (stream.detachTimer) {
    clearTimeout(stream.detachTimer);
    stream.detachTimer = null;
  }

  const listener = { onNext, onError };
  stream.listeners.add(listener);

  if (valueCache.has(key)) {
    onNext(cachedSnapshot(valueCache.get(key).value), { source: 'cache' });
  }

  if (!stream.active) {
    const unsubscribe = onValue(reference, (snapshot) => {
      valueCache.set(key, { value: snapshot.val(), updatedAt: Date.now() });
      Array.from(stream.listeners).forEach((subscriber) => {
        subscriber.onNext(snapshot, { source: 'network' });
      });
    }, (error) => {
      stream.active = false;
      stream.unsubscribe = null;
      Array.from(stream.listeners).forEach((subscriber) => {
        if (subscriber.onError) subscriber.onError(error);
      });
    });
    stream.unsubscribe = typeof unsubscribe === 'function' ? unsubscribe : null;
    stream.active = typeof unsubscribe === 'function';
  }

  return () => {
    stream.listeners.delete(listener);
    if (stream.listeners.size > 0) return;
    if (!stream.active) {
      streams.delete(key);
      return;
    }
    stream.detachTimer = setTimeout(() => stopStream(key, stream), STREAM_RETENTION_MS);
    if (typeof stream.detachTimer?.unref === 'function') stream.detachTimer.unref();
  };
}

export function getCachedFirebaseValue(cacheKey) {
  return valueCache.has(String(cacheKey)) ? valueCache.get(String(cacheKey)).value : undefined;
}

export function setCachedDataValue(cacheKey, value) {
  const key = String(cacheKey || '');
  if (!key) throw new Error('A cache key is required when caching data.');
  valueCache.set(key, { value, updatedAt: Date.now() });
}

export async function getAndCacheFirebaseValue(reference, cacheKey) {
  const key = String(cacheKey || '');
  if (!key) throw new Error('A cache key is required for Firebase reads.');
  const snapshot = await get(reference);
  valueCache.set(key, { value: snapshot.val(), updatedAt: Date.now() });
  return snapshot;
}

export function clearCachedFirebaseData() {
  streams.forEach((stream, key) => {
    if (stream.detachTimer) clearTimeout(stream.detachTimer);
    stopStream(key, stream);
  });
  streams.clear();
  valueCache.clear();
}
